import { ApiError, isGuest, type Lang, request, withPicture } from '@/api/client';
import type { Question } from '@/api/questions';
import { checked, PRACTICE_SESSION } from '@/api/shape';

export interface PracticeDrill {
  lang: Lang;
  label?: string;
  topic?: number;
  only_wrong?: boolean;
  review?: boolean;
  numeric?: boolean;
  tricky?: boolean;
  unsolved?: boolean;
  bilet?: number;
  lesson?: number;
  skip?: number;
  limit?: number;
  question_ids?: string[];
}

export interface PracticeSession {
  id: string;
  lang: Lang;
  label: string;
  status: 'in_progress' | 'finished' | 'abandoned';
  position: number;
  total: number;
  created_at: string;
  updated_at: string;
  questions: Question[];
}

const fixDrillMedia = (drill: PracticeSession): PracticeSession => ({
  ...drill,
  questions: drill.questions.map(withPicture),
});

export function startPracticeSession(body: PracticeDrill): Promise<PracticeSession> {
  if (isGuest()) return Promise.reject(new ApiError(401, 'guest'));
  return request<PracticeSession>('/me/practice-sessions?slim=true', {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(fixDrillMedia);
}

export function getCurrentPracticeSession(): Promise<PracticeSession | null> {
  if (isGuest()) return Promise.resolve(null);
  return request<unknown>('/me/practice-sessions/current')
    .then(checked<PracticeSession>(PRACTICE_SESSION, 'practice session'))
    .then(fixDrillMedia)
    .catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    });
}

export function updatePracticeSession(
  id: string,
  body: { position?: number; status?: 'finished' | 'abandoned' },
): Promise<PracticeSession> {
  return request<PracticeSession>(`/me/practice-sessions/${id}?slim=true`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }).then(fixDrillMedia);
}
